import { z } from 'zod';

import { type AgentCategory } from './flowAgent.js';
import { type FlowData } from './flowDefinition.js';
import { type Tag, tagsQuerySchema } from './tag.js';
import { isDefined } from '../../utils.js';
import { type BaseItem } from '../common.js';

export enum TemplateVisibility {
  Private = 'Private',
  Organization = 'Organization',
  Global = 'Global',
}

export interface TemplateTag {
  name: string;
  category: TagCategory;
}

interface WithThumbnailUrl {
  thumbnailUrl?: string;
}

interface BaseFlowDefinitionTemplate {
  name: string;
  organizationId: string;
  data: FlowData;
  content: string;
  detailedContentMarkdown: string;
  agentTags: string[]; // Deprecated, use tags instead.
  agentCategory: AgentCategory; // Deprecated, use tags instead.
  createdById?: string;
  updatedById?: string;
  metadata?: Record<string, any>;
  tags?: Tag[];
  visibility: TemplateVisibility;
}

export interface FlowDefinitionTemplate
  extends BaseFlowDefinitionTemplate, BaseItem, WithThumbnailUrl {
  flowDefinitionId: string | undefined;
  isBookmarked?: boolean;
}

type WithOptionalKeys<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type FlowDefinitionUpdateValues = Omit<BaseFlowDefinitionTemplate, 'tags'> & {
  tags?: TemplateTag[];
};

export type CreateValues = WithOptionalKeys<FlowDefinitionUpdateValues, 'organizationId'>;
export type UpdateValues = Partial<Omit<FlowDefinitionUpdateValues, 'data'>>;

export const findAllOptionsSchema = z.object({
  organizationId: z.array(z.string().uuid()).optional(),
  creatorId: z.string().uuid().optional(),
  tags: tagsQuerySchema
    .optional()
    .transform((val) => (isDefined(val) ? JSON.stringify(val) : undefined)),
  bookmarkedByUserId: z.string().uuid().optional(),
  queryUserId: z.string().uuid().optional(),

  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  visibility: z.array(z.nativeEnum(TemplateVisibility)).optional(),
});
export type FindAllOptions = z.input<typeof findAllOptionsSchema>;

export enum TagCategory {
  OperationalGroup = 'OperationalGroup',
  Agent = 'Agent',
  FlowType = 'FlowType',
  ReplaceVendor = 'ReplaceVendor',
}

export const thumbnailUploadSchema = z.object({
  thumbnailData: z.string().base64(),
  contentType: z.string(),
});
export type ThumbnailUploadRequest = z.infer<typeof thumbnailUploadSchema>;

export const thumbnailUploadResponseSchema = z.object({
  thumbnailUrl: z.string().url(),
});
export type ThumbnailUploadResponse = z.infer<typeof thumbnailUploadResponseSchema>;
